import { useEffect, useState } from "react";
import { Copy, Download, FileText, FlaskConical, KeyRound, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, ts } from "@/lib/api";
import { Panel, btn, field } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";

const RECEIVE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/as2/receive`;
const FIELDS = [["ep_as2_id", "EP AS2 ID"], ["go_as2_id", "Group O AS2 ID"], ["go_as2_url", "Group O AS2 URL"], ["go_sending_ip", "Group O sending IP"], ["as2_enc_alg", "Encryption"], ["as2_digest", "Signing digest"], ["as2_mdn_mode", "MDN mode (SYNC / ASYNC)"], ["edi_contact_name", "EP EDI contact name"], ["edi_contact_email", "EP EDI contact email"], ["edi_contact_phone", "EP EDI contact phone"], ["ack_overdue_hours", "Flag missing 997 after (hours)"]];
const on = (v) => v === true || String(v).toLowerCase() === "true";

const RegenDialog = ({ open, close, done }) => {
  const [pw, setPw] = useState("");
  const go = () => api.post("/as2/generate-cert", { password: pw }).then(() => { toast.success("New EP certificate generated - send it to every partner now"); setPw(""); close(); done(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setPw(""); close(); } }}>
      <DialogContent className="max-w-lg border-slate-800 bg-[#1F232C] text-slate-100" data-testid="as2-regen-dialog">
        <DialogHeader><DialogTitle>Replace the Euro Packaging AS2 certificate?</DialogTitle></DialogHeader>
        <div className="rounded-sm border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200" data-testid="as2-regen-warning">
          Every trading partner (Group O, Casino, Delhaize and others) holds the current certificate. As soon as a new one is generated, they will reject your files and you won't be able to read theirs until each of them has installed the new certificate. The old one cannot be restored.
        </div>
        <label className="block"><span className="mb-1 block font-mono text-[10px] uppercase text-slate-500">Your password to confirm</span>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && pw && go()} className={field} data-testid="as2-regen-password" /></label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close} className={btn.ghost} data-testid="as2-regen-cancel">Cancel</Button>
          <Button onClick={go} disabled={!pw} className="bg-rose-600 text-white hover:bg-rose-500" data-testid="as2-regen-confirm">Replace certificate</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Cert = ({ label, info, testId }) => (
  <div className="rounded-sm border border-slate-800 bg-[#17191F] p-3 text-xs" data-testid={testId}>
    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    {info ? (
      <div className="mt-1 space-y-0.5 font-mono text-slate-300">
        <div className="text-emerald-300">{info.subject}</div>
        <div>Expires {new Date(info.expires).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}</div>
        <div className="break-all text-[10px] text-slate-500">{info.fingerprint}</div>
      </div>
    ) : <div className="mt-1 text-amber-300">Not set</div>}
  </div>
);

export const As2Panel = ({ cfg, setCfg, isMgr }) => {
  const [st, setSt] = useState(null);
  const [pem, setPem] = useState("");
  const [test, setTest] = useState(null);
  const [plist, setPlist] = useState([]);
  const [sheetP, setSheetP] = useState("GROUPO");
  const load = () => api.get("/as2/status").then((r) => setSt(r.data));
  useEffect(() => {
    load();
    api.get("/partners").then((r) => setPlist(r.data.partners.filter((p) => p.active !== false)));
  }, []);
  const act = async (fn, msg) => {
    try {
      const r = await fn();
      toast.success(msg);
      load();
      return r;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const downloadCert = async () => {
    const r = await api.get("/as2/ep-certificate", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "euro_packaging_as2.pem";
    a.click();
  };
  const [ip, setIp] = useState(null);
  const [regen, setRegen] = useState(false);
  const checkIp = async () => {
    try {
      setIp((await api.post("/as2/outgoing-ip")).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const downloadSheet = async (path = "/as2/details-sheet", name = `EPG_EDI_AS2_Connection_Details_${sheetP}.pdf`, partner = sheetP) => {
    try {
      const r = await api.get(path, { params: { receive_url: RECEIVE_URL, partner }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = name;
      a.click();
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const onPemFile = (e) => e.target.files?.[0]?.text().then(setPem);

  return (
    <Panel title="AS2 direct connection to Group O" testId="as2-panel"
      action={st && <StatusBadge status={st.enabled && !st.missing.length ? "processed" : "ready"} label={st.enabled ? (st.missing.length ? "Enabled · incomplete" : "Live") : "Off · mocked"} testId="as2-status-badge" />}>
      <div className="space-y-5 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <select value={sheetP} onChange={(e) => setSheetP(e.target.value)} className={`h-9 rounded-md px-2 text-sm ${field}`} data-testid="as2-sheet-partner">
            {plist.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
          <Button onClick={() => downloadSheet()} className={btn.primary} data-testid="as2-details-sheet-btn"><FileText className="mr-1.5 h-4 w-4" /> Download AS2 details sheet (PDF)</Button>
          <Button onClick={() => downloadSheet("/as2/confirmation-sheet", "EP_GroupO_AS2_Connection_Confirmation.pdf", "GROUPO")} variant="ghost" className={btn.ghost} data-testid="as2-confirmation-sheet-btn"><FileText className="mr-1.5 h-4 w-4" /> Download connection confirmation (PDF)</Button>
          <span className="text-xs text-slate-500">Details sheet = our side only. Confirmation = our and Group O's settings side by side, with status and sign-off.</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Switch checked={on(cfg.as2_enabled)} disabled={!isMgr} onCheckedChange={(v) => setCfg({ ...cfg, as2_enabled: v })} data-testid="as2-enabled-switch" />
          <span className="text-sm text-slate-200">Send outbound 940 / 850 via AS2 when "Transmit" is clicked</span>
          <span className="text-xs text-slate-500">(save settings to apply)</span>
        </div>
        {st?.missing?.length > 0 && (
          <div className="rounded-sm border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="as2-missing">Still needed before go-live: {st.missing.join(", ")}</div>
        )}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FIELDS.map(([k, l]) => (
            <label key={k} className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">{l}</span>
              <Input value={cfg[k] ?? ""} disabled={!isMgr} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} className={field} data-testid={`as2-${k}`} />
            </label>
          ))}
          {[["as2_sign", "Sign messages"], ["as2_encrypt", "Encrypt messages"], ["auto_send", "Auto-send 940 / 850 as soon as generated"], ["send_997", "Send 997 for every inbound file"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-3 pt-5 text-sm text-slate-200">
              <Switch checked={on(cfg[k])} disabled={!isMgr} onCheckedChange={(v) => setCfg({ ...cfg, [k]: v })} data-testid={`as2-${k}`} /> {l}
            </label>
          ))}
        </div>
        <div className="rounded-sm border border-slate-800 bg-[#17191F] p-4" data-testid="as2-proxy-section">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Static-IP proxy for outgoing AS2 (optional, e.g. QuotaGuard / Fixie)</span>
          <div className="flex flex-wrap gap-2">
            <Input value={cfg.as2_proxy_url ?? ""} disabled={!isMgr} onChange={(e) => setCfg({ ...cfg, as2_proxy_url: e.target.value })} placeholder="http://user:password@proxy-host:port" className={`min-w-[280px] flex-1 font-mono text-xs ${field}`} data-testid="as2-proxy-url" />
            {isMgr && <Button variant="ghost" onClick={checkIp} className={btn.ghost} data-testid="as2-check-ip-btn">Check outgoing IP</Button>}
          </div>
          <p className="mt-2 text-xs text-slate-500">All AS2 sends to Group O go through this proxy so they leave from its fixed IPs. Save settings first, then check the IP and give it to Group O for their allow-list.</p>
          {ip && (
            <div className="mt-2 flex flex-wrap gap-4 font-mono text-xs" data-testid="as2-ip-result">
              <span className="text-slate-400">Direct: <b className="text-slate-100">{ip.direct}</b></span>
              {ip.via_proxy && <span className="text-slate-400">Via proxy: <b className="text-green-300">{ip.via_proxy}</b></span>}
            </div>
          )}
        </div>
        <div>
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Our AS2 receive URL</span>
          <div className="flex gap-2">
            <Input readOnly value={RECEIVE_URL} className={`font-mono text-xs ${field}`} data-testid="as2-receive-url" />
            <Button variant="ghost" onClick={() => navigator.clipboard?.writeText(RECEIVE_URL).then(() => toast.success("Copied"))} className={btn.ghost} data-testid="as2-copy-url-btn"><Copy className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <Cert label="Euro Packaging certificate (send to Group O)" info={st?.ep_cert} testId="as2-ep-cert" />
            {isMgr && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => (st?.ep_cert ? setRegen(true) : act(() => api.post("/as2/generate-cert", {}), "New EP certificate generated"))} className={btn.ghost} variant="ghost" data-testid="as2-generate-cert-btn"><KeyRound className="mr-1.5 h-3.5 w-3.5" /> {st?.ep_cert ? "Regenerate" : "Generate"}</Button>
                <RegenDialog open={regen} close={() => setRegen(false)} done={load} />
                {st?.ep_cert && <Button size="sm" variant="ghost" onClick={downloadCert} className={btn.ghost} data-testid="as2-download-cert-btn"><Download className="mr-1.5 h-3.5 w-3.5" /> Download .pem</Button>}
                {st?.ep_cert && <Button size="sm" onClick={async () => setTest((await act(() => api.post("/as2/self-test"), "Self-test complete"))?.data)} className={btn.primary} data-testid="as2-self-test-btn"><FlaskConical className="mr-1.5 h-3.5 w-3.5" /> Self-test</Button>}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Cert label="Group O certificate" info={st?.go_cert} testId="as2-go-cert" />
            {isMgr && (
              <>
                <Textarea value={pem} onChange={(e) => setPem(e.target.value)} placeholder="-----BEGIN CERTIFICATE----- ..." className={`h-20 font-mono text-[10px] ${field}`} data-testid="as2-partner-pem-input" />
                <div className="flex gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800">
                    <Upload className="h-3.5 w-3.5" /> .pem / .cer file
                    <input type="file" accept=".pem,.cer,.crt" className="hidden" onChange={onPemFile} data-testid="as2-partner-pem-file" />
                  </label>
                  <Button size="sm" disabled={!pem.trim()} onClick={() => act(() => api.post("/as2/partner-cert", { pem }), "Group O certificate saved")} className={btn.primary} data-testid="as2-save-partner-cert-btn"><ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Save certificate</Button>
                </div>
              </>
            )}
          </div>
        </div>
        {test && (
          <div className="space-y-1 rounded-sm border border-slate-800 bg-[#17191F] p-3 text-xs" data-testid="as2-self-test-result">
            {test.steps.map((s) => <div key={s.step} className="flex justify-between gap-3"><span className="text-slate-300">{s.step}</span><StatusBadge status={s.result === "processed" ? "processed" : "error"} label={s.result} /></div>)}
          </div>
        )}
        {st?.recent?.length > 0 && (
          <div className="text-xs" data-testid="as2-recent">
            <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">Recent inbound AS2</div>
            {st.recent.map((r) => (
              <div key={r.id} className="flex flex-wrap gap-3 border-t border-slate-800 py-1.5 text-slate-300">
                <span className="font-mono text-slate-500">{ts(r.at)}</span><span>{r.from}</span><StatusBadge status={r.status} />
                <span>{r.results?.map((x) => `${x.doc_type} ${x.status}`).join(", ") || r.error}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
};
